import { useState } from "react";
import { CheckCircle2, MessageCircle, Loader2, Calendar, User, Phone, Mail, Clock, Stethoscope } from "lucide-react";
import { CLINIC, TREATMENTS, buildWhatsAppLink } from "../lib/clinic";

const TIMES = [
  "09:30 AM", "10:00 AM", "10:30 AM", "11:00 AM", "11:30 AM",
  "12:00 PM", "12:30 PM", "02:00 PM", "02:30 PM", "03:00 PM",
  "03:30 PM", "04:00 PM", "04:30 PM", "05:00 PM", "05:30 PM",
  "06:00 PM", "06:30 PM",
];

const today = () => new Date().toISOString().split("T")[0];

export const Appointment = () => {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    date: "",
    time: "",
    treatment: "",
    message: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  const set = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: null }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Please enter your name";
    if (!form.phone.trim()) e.phone = "Please enter a phone number";
    else if (!/^[0-9+\s-]{7,15}$/.test(form.phone.trim()))
      e.phone = "Please enter a valid phone number";
    if (form.email && !/^\S+@\S+\.\S+$/.test(form.email))
      e.email = "Please enter a valid email";
    if (!form.date) e.date = "Please choose a date";
    if (!form.time) e.time = "Please choose a time";
    if (!form.treatment) e.treatment = "Please pick a treatment";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const submit = (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);

    const message =
      `Hello Abhayam Dental Clinic,\n\n` +
      `I would like to book an appointment.\n\n` +
      `*Name:* ${form.name}\n` +
      `*Phone:* ${form.phone}\n` +
      (form.email ? `*Email:* ${form.email}\n` : "") +
      `*Preferred Date:* ${form.date}\n` +
      `*Preferred Time:* ${form.time}\n` +
      `*Treatment:* ${form.treatment}\n` +
      (form.message ? `*Message:* ${form.message}\n` : "") +
      `\nThank you.`;

    const link = buildWhatsAppLink(message);

    setTimeout(() => {
      window.open(link, "_blank", "noopener,noreferrer");
      setLoading(false);
      setSuccess(true);
    }, 600);
  };

  const reset = () => {
    setSuccess(false);
    setForm({
      name: "",
      phone: "",
      email: "",
      date: "",
      time: "",
      treatment: "",
      message: "",
    });
  };

  return (
    <section
      id="appointment"
      data-testid="appointment-section"
      className="relative py-20 sm:py-28 bg-white overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-[#4FC3F7]/10 rounded-full blur-3xl translate-x-1/3 -translate-y-1/4" />
        <div className="absolute bottom-0 left-0 w-[500px] h-[500px] bg-[#009688]/8 rounded-full blur-3xl -translate-x-1/3 translate-y-1/4" />
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-12 gap-10 lg:gap-14 items-start">
          <div className="lg:col-span-5 reveal lg:sticky lg:top-28">
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-[#009688]/10 text-[#009688] text-xs font-semibold tracking-wide uppercase">
              Book Appointment
            </div>
            <h2 className="mt-5 text-4xl sm:text-5xl font-semibold tracking-tight text-[#1F2937] leading-[1.05]">
              Schedule your visit{" "}
              <span className="text-[#009688]">in seconds</span>.
            </h2>
            <p className="mt-5 text-lg text-[#1F2937]/65">
              Fill in your preferred date, time and treatment. We&apos;ll receive
              your request directly on WhatsApp and confirm shortly.
            </p>

            <div className="mt-8 space-y-4">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#009688]/10 grid place-items-center shrink-0">
                  <Clock className="w-5 h-5 text-[#009688]" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#1F2937]">
                    Opening Hours
                  </div>
                  <div className="text-sm text-[#1F2937]/60 mt-0.5">
                    {CLINIC.hours.weekday}
                  </div>
                  <div className="text-sm text-[#1F2937]/60">
                    {CLINIC.hours.sunday}
                  </div>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-xl bg-[#009688]/10 grid place-items-center shrink-0">
                  <Phone className="w-5 h-5 text-[#009688]" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#1F2937]">
                    Prefer to call?
                  </div>
                  <a
                    href={`tel:${CLINIC.phoneRaw}`}
                    className="text-sm text-[#009688] font-semibold mt-0.5 block hover:underline"
                  >
                    {CLINIC.phone}
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Form */}
          <div className="lg:col-span-7">
            <form
              onSubmit={submit}
              data-testid="appointment-form"
              className="bg-white rounded-3xl border border-[#009688]/12 p-6 sm:p-8 shadow-[0_30px_60px_-20px_rgba(0,150,136,0.15)]"
            >
              <div className="grid sm:grid-cols-2 gap-4 sm:gap-5">
                <Field
                  label="Full Name"
                  icon={User}
                  error={errors.name}
                  input={
                    <input
                      data-testid="input-name"
                      type="text"
                      value={form.name}
                      onChange={(e) => set("name", e.target.value)}
                      placeholder="Ashiq Rahman"
                      className="w-full outline-none bg-transparent"
                    />
                  }
                />
                <Field
                  label="Phone Number"
                  icon={Phone}
                  error={errors.phone}
                  input={
                    <input
                      data-testid="input-phone"
                      type="tel"
                      value={form.phone}
                      onChange={(e) => set("phone", e.target.value)}
                      placeholder="+91 98765 43210"
                      className="w-full outline-none bg-transparent"
                    />
                  }
                />
                <Field
                  label="Email (optional)"
                  icon={Mail}
                  error={errors.email}
                  input={
                    <input
                      data-testid="input-email"
                      type="email"
                      value={form.email}
                      onChange={(e) => set("email", e.target.value)}
                      placeholder="you@example.com"
                      className="w-full outline-none bg-transparent"
                    />
                  }
                />
                <Field
                  label="Preferred Treatment"
                  icon={Stethoscope}
                  error={errors.treatment}
                  input={
                    <select
                      data-testid="input-treatment"
                      value={form.treatment}
                      onChange={(e) => set("treatment", e.target.value)}
                      className="w-full outline-none bg-transparent appearance-none pr-4"
                    >
                      <option value="">Select a treatment</option>
                      {TREATMENTS.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  }
                />
                <Field
                  label="Preferred Date"
                  icon={Calendar}
                  error={errors.date}
                  input={
                    <input
                      data-testid="input-date"
                      type="date"
                      min={today()}
                      value={form.date}
                      onChange={(e) => set("date", e.target.value)}
                      className="w-full outline-none bg-transparent"
                    />
                  }
                />
                <Field
                  label="Preferred Time"
                  icon={Clock}
                  error={errors.time}
                  input={
                    <select
                      data-testid="input-time"
                      value={form.time}
                      onChange={(e) => set("time", e.target.value)}
                      className="w-full outline-none bg-transparent appearance-none pr-4"
                    >
                      <option value="">Select a time slot</option>
                      {TIMES.map((t) => (
                        <option key={t} value={t}>
                          {t}
                        </option>
                      ))}
                    </select>
                  }
                />
              </div>

              <div className="mt-5">
                <label className="text-xs font-semibold text-[#1F2937]/60 uppercase tracking-wider">
                  Message (optional)
                </label>
                <textarea
                  data-testid="input-message"
                  rows="4"
                  value={form.message}
                  onChange={(e) => set("message", e.target.value)}
                  placeholder="Tell us more about your concern…"
                  className="mt-2 w-full rounded-2xl border border-[#009688]/15 bg-[#F7FAFC]/40 p-4 outline-none text-[15px] text-[#1F2937] placeholder-[#1F2937]/35 focus:border-[#009688] focus:bg-white transition"
                />
              </div>

              <button
                type="submit"
                data-testid="submit-appointment"
                disabled={loading}
                className="mt-6 w-full flex items-center justify-center gap-2 py-4 rounded-full bg-[#009688] text-white font-semibold hover:bg-[#00796b] active:scale-[0.99] transition-all disabled:opacity-70 shadow-lg shadow-[#009688]/25"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Preparing WhatsApp…
                  </>
                ) : (
                  <>
                    <MessageCircle className="w-5 h-5" />
                    Book via WhatsApp
                  </>
                )}
              </button>

              <p className="mt-3 text-center text-xs text-[#1F2937]/50">
                By submitting, you agree to be contacted by Abhayam Dental Clinic.
              </p>
            </form>
          </div>
        </div>
      </div>

      {/* Success Modal */}
      {success && (
        <div
          data-testid="appointment-success-modal"
          className="fixed inset-0 z-[110] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
        >
          <div className="w-full max-w-md bg-white rounded-3xl p-8 text-center shadow-2xl animate-[fadeIn_0.3s_ease-out]">
            <div className="w-20 h-20 rounded-full bg-[#009688]/10 mx-auto grid place-items-center">
              <CheckCircle2 className="w-11 h-11 text-[#009688]" strokeWidth={2} />
            </div>
            <h3 className="mt-5 text-2xl font-semibold text-[#1F2937]">
              Request Sent!
            </h3>
            <p className="mt-2 text-sm text-[#1F2937]/65 leading-relaxed">
              Your appointment request has been prepared on WhatsApp. Just tap
              send and our team will confirm shortly.
            </p>
            <button
              data-testid="success-close-btn"
              onClick={reset}
              className="mt-6 w-full py-3 rounded-full bg-[#009688] text-white font-semibold hover:bg-[#00796b] transition-colors"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </section>
  );
};

const Field = ({ label, icon: Icon, error, input }) => (
  <div>
    <label className="text-xs font-semibold text-[#1F2937]/60 uppercase tracking-wider">
      {label}
    </label>
    <div
      className={`mt-2 flex items-center gap-3 rounded-2xl border ${
        error ? "border-red-400" : "border-[#009688]/15"
      } bg-[#F7FAFC]/40 px-4 py-3.5 focus-within:border-[#009688] focus-within:bg-white transition`}
    >
      <Icon className="w-4 h-4 text-[#009688] shrink-0" />
      <div className="flex-1 text-[15px] text-[#1F2937]">{input}</div>
    </div>
    {error && (
      <div className="mt-1.5 text-xs text-red-500 font-medium">{error}</div>
    )}
  </div>
);
